import React, { useState } from 'react';
import { useLocation, Link, Routes, Route, Navigate } from 'react-router-dom';
import ApiService from '../../services/api';
import { getToken } from '../../utils/auth';
import { useNotification } from '../../context/NotificationContext';
import ReactJson from '@microlink/react-json-view';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';

// Initialize dayjs plugins
dayjs.extend(relativeTime);

// Define types for the search response
interface SearchPagination {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

interface SearchResponse {
  results: Array<{
    tag: {
      id: number;
      name: string;
      value: any;
      createdAt?: string;
      updatedAt?: string;
      score: number;
    };
    ingestions: Array<{
      id: string;
      createdAt?: string;
      updatedAt?: string;
      type?: string;
      userId?: string;
      data?: any;
    }>;
  }>;
  pagination: SearchPagination;
}

// Intel Search Component
const IntelSearch = () => {
  const [query, setQuery] = useState<string>('');
  const [results, setResults] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [sortBy, setSortBy] = useState<string>('score'); // Default sort by score
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0,
  });
  const { showNotification } = useNotification();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!query.trim()) return;

    setLoading(true);

    try {
      const token = getToken();
      if (!token) {
        showNotification('API token not found. Please log in again.', 'error');
        return;
      }

      const api = new ApiService(token);
      const apiResponse = await api.searchIntel(query, pagination.page, pagination.limit);
      const response = apiResponse as unknown as SearchResponse;
      
      // Handle the response structure
      if (response && typeof response === 'object') {
        setResults(Array.isArray(response.results) ? response.results : []);
        
        if (response.pagination) {
          setPagination({
            page: Number(response.pagination.page) || 1,
            limit: Number(response.pagination.limit) || 10,
            total: Number(response.pagination.total) || 0,
            totalPages: Number(response.pagination.totalPages) || 0,
          });
        }
      } else {
        setResults([]);
        setPagination({
          page: 1,
          limit: 10,
          total: 0,
          totalPages: 0,
        });
      }
    } catch (error) {
      console.error('Error searching Intel:', error);
      showNotification('Failed to search Intel. Please try again.', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = async (newPage: number) => {
    if (newPage === pagination.page || newPage < 1 || newPage > pagination.totalPages) return;
    
    setLoading(true);
    
    try {
      const token = getToken();
      if (!token) {
        showNotification('API token not found. Please log in again.', 'error');
        return;
      }

      const api = new ApiService(token);
      const apiResponse = await api.searchIntel(query, newPage, pagination.limit);
      const response = apiResponse as unknown as SearchResponse;
      
      // Handle the response structure
      if (response && typeof response === 'object') {
        setResults(Array.isArray(response.results) ? response.results : []);
        
        if (response.pagination) {
          setPagination({
            page: Number(response.pagination.page) || newPage,
            limit: Number(response.pagination.limit) || pagination.limit,
            total: Number(response.pagination.total) || 0,
            totalPages: Number(response.pagination.totalPages) || 0,
          });
        }
      } else {
        setResults([]);
      }
      
      // Scroll to top of results
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (error) {
      console.error('Error changing page:', error);
      showNotification('Failed to load page. Please try again.', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Function to sort results based on selected criteria
  const getSortedResults = () => {
    if (!results.length) return [];
    
    const sortedResults = [...results];
    
    switch (sortBy) {
      case 'score':
        return sortedResults.sort((a, b) => b.tag.score - a.tag.score);
      case 'date':
        return sortedResults.sort((a, b) => {
          const dateA = a.tag.createdAt ? new Date(a.tag.createdAt).getTime() : 0;
          const dateB = b.tag.createdAt ? new Date(b.tag.createdAt).getTime() : 0;
          return dateB - dateA; // Newest first
        });
      case 'score_date':
        return sortedResults.sort((a, b) => {
          // First sort by score
          const scoreDiff = b.tag.score - a.tag.score;
          if (Math.abs(scoreDiff) > 0.1) return scoreDiff; // If score difference is significant
          
          // If scores are close, sort by date
          const dateA = a.tag.createdAt ? new Date(a.tag.createdAt).getTime() : 0;
          const dateB = b.tag.createdAt ? new Date(b.tag.createdAt).getTime() : 0;
          return dateB - dateA;
        });
      case 'date_score':
        return sortedResults.sort((a, b) => {
          // First group by date (day level)
          const dateA = a.tag.createdAt ? new Date(a.tag.createdAt).setHours(0,0,0,0) : 0;
          const dateB = b.tag.createdAt ? new Date(b.tag.createdAt).setHours(0,0,0,0) : 0;
          
          if (dateA !== dateB) return dateB - dateA; // Different days
          
          // Same day, sort by score
          return b.tag.score - a.tag.score;
        });
      default:
        return sortedResults;
    }
  };

  return (
    <div>
      <h2 className="text-xl font-semibold mb-4">Intel Search</h2>

      <form
        onSubmit={handleSubmit}
        className="mb-6 bg-white p-4 rounded shadow"
      >
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-grow">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Search Query
            </label>
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="w-full p-2 border border-gray-300 rounded"
              placeholder="Enter search query..."
              required
            />
          </div>

          <div className="flex items-end">
            <button
              type="submit"
              className="bg-blue-600 text-white py-2 px-4 rounded hover:bg-blue-700 w-full md:w-auto"
              disabled={loading}
            >
              {loading ? 'Searching...' : 'Search'}
            </button>
          </div>
        </div>
      </form>

      {results.length > 0 ? (
        <div className="bg-white p-4 rounded shadow">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-medium">
              Search Results 
              {pagination.total > 0 && (
                <span className="text-sm text-gray-500 ml-2">
                  (Showing {(pagination.page - 1) * pagination.limit + 1}-
                  {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total})
                </span>
              )}
            </h3>
            
            <div className="flex items-center">
              <label className="text-sm font-medium text-gray-700 mr-2">
                Sort by:
              </label>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="p-1 text-sm border border-gray-300 rounded"
              >
                <option value="score">Relevance</option>
                <option value="date">Newest</option>
                <option value="score_date">Relevance, then Date</option>
                <option value="date_score">Date, then Relevance</option>
              </select>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {getSortedResults().map(({ tag, ingestions }, index) => (
              <div key={index} className="border border-gray-200 rounded p-4">
                <h5 className="font-medium text-blue-600 mb-2">
                  {tag.name} ({tag.score.toFixed(2)})
                </h5>
                {tag.createdAt && (
                  <p className="text-xs text-gray-500 mb-2">
                    Created: {dayjs(tag.createdAt).format('MMM D, YYYY h:mm A')} ({dayjs(tag.createdAt).fromNow()})
                  </p>
                )}
                {tag.value && (
                  <div className="text-sm text-gray-600 mb-2">
                    {
                      typeof tag.value === 'string' ? tag.value : (
                        <ReactJson 
                          src={tag.value} 
                          name={false}
                          collapsed={1}
                          displayDataTypes={false}
                          enableClipboard={false}
                          theme="rjv-default"
                          style={{ fontSize: '12px' }}
                        />
                      )
                    }
                  </div>
                )}
                {ingestions.length > 0 && (
                  <div className="mt-2">
                    <h5 className="text-xs font-medium text-gray-500 uppercase mb-1">
                      Metadata
                    </h5>
                    <div className="text-sm">
                      {ingestions.map((ingestion: any) => (
                        <div key={ingestion.id} className="flex flex-col mb-3">
                          <div className="flex justify-between items-center mb-1">
                            <span className="font-medium">{ingestion.id}</span>
                            {ingestion.createdAt && (
                              <span className="text-xs text-gray-500">
                                {dayjs(ingestion.createdAt).fromNow()}
                              </span>
                            )}
                          </div>
                          {ingestion.data && (
                            <div className="border border-gray-200 rounded p-2 overflow-auto max-h-60">
                              <ReactJson 
                                src={ingestion.data} 
                                name={false}
                                collapsed={1}
                                displayDataTypes={false}
                                enableClipboard={false}
                                theme="rjv-default"
                                style={{ fontSize: '12px' }}
                              />
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Pagination Controls */}
          {pagination.totalPages > 1 && (
            <div className="flex justify-center mt-6">
              <nav className="flex items-center">
                <button
                  onClick={() => handlePageChange(pagination.page - 1)}
                  disabled={pagination.page === 1 || loading}
                  className={`px-3 py-1 rounded-l border ${
                    pagination.page === 1 || loading
                      ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                      : 'bg-white text-blue-600 hover:bg-blue-50'
                  }`}
                >
                  Previous
                </button>
                
                <div className="flex">
                  {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                    // Show pages around current page
                    let pageToShow;
                    if (pagination.totalPages <= 5) {
                      pageToShow = i + 1;
                    } else if (pagination.page <= 3) {
                      pageToShow = i + 1;
                    } else if (pagination.page >= pagination.totalPages - 2) {
                      pageToShow = pagination.totalPages - 4 + i;
                    } else {
                      pageToShow = pagination.page - 2 + i;
                    }
                    
                    return (
                      <button
                        key={pageToShow}
                        onClick={() => handlePageChange(pageToShow)}
                        disabled={loading}
                        className={`px-3 py-1 border-t border-b ${
                          pageToShow === pagination.page
                            ? 'bg-blue-600 text-white'
                            : 'bg-white text-blue-600 hover:bg-blue-50'
                        }`}
                      >
                        {pageToShow}
                      </button>
                    );
                  })}
                </div>
                
                <button
                  onClick={() => handlePageChange(pagination.page + 1)}
                  disabled={pagination.page === pagination.totalPages || loading}
                  className={`px-3 py-1 rounded-r border ${
                    pagination.page === pagination.totalPages || loading
                      ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                      : 'bg-white text-blue-600 hover:bg-blue-50'
                  }`}
                >
                  Next
                </button>
              </nav>
            </div>
          )}
        </div>
      ) : loading ? (
        <div className="bg-white p-4 rounded shadow text-center">
          <p>Searching...</p>
        </div>
      ) : (
        query && (
          <div className="bg-white p-4 rounded shadow text-center">
            <p>No results found for "{query}"</p>
          </div>
        )
      )}
    </div>
  );
};

// Web Search Component
const WebSearch = () => {
  const [query, setQuery] = useState<string>('');
  const [source, setSource] = useState<string>('');
  const [results, setResults] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const { showNotification } = useNotification();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!query.trim()) return;

    setLoading(true);

    try {
      const token = getToken();
      if (!token) {
        showNotification('API token not found. Please log in again.', 'error');
        return;
      }

      const api = new ApiService(token);
      const result = await api.searchWeb(query, source || undefined);
      setResults(Array.isArray(result) ? result : []);
    } catch (error) {
      console.error('Error searching web:', error);
      showNotification('Failed to search web. Please try again.', 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <h2 className="text-xl font-semibold mb-4">Web Search</h2>

      <form
        onSubmit={handleSubmit}
        className="mb-6 bg-white p-4 rounded shadow"
      >
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Search Query
            </label>
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="w-full p-2 border border-gray-300 rounded"
              placeholder="Enter search query..."
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Source (Optional)
            </label>
            <select
              value={source}
              onChange={(e) => setSource(e.target.value)}
              className="w-full p-2 border border-gray-300 rounded"
            >
              <option value="">Random (Default)</option>
              <option value="tavily">Tavily</option>
              <option value="serpapi">SerpAPI</option>
              <option value="zyte">Zyte</option>
            </select>
          </div>

          <div className="md:col-span-3">
            <button
              type="submit"
              className="bg-blue-600 text-white py-2 px-4 rounded hover:bg-blue-700 w-full"
              disabled={loading}
            >
              {loading ? 'Searching...' : 'Search Web'}
            </button>
          </div>
        </div>
      </form>

      {results.length > 0 ? (
        <div className="bg-white p-4 rounded shadow">
          <h3 className="text-lg font-medium mb-4">Web Search Results</h3>

          <div className="space-y-6">
            {results.map((result, index) => (
              <div
                key={index}
                className="border-b border-gray-200 pb-4 last:border-b-0 last:pb-0"
              >
                <h4 className="font-medium text-blue-600 mb-1">
                  {result.url ? (
                    <a
                      href={result.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="hover:underline"
                    >
                      {result.name || result.title || 'Untitled'}
                    </a>
                  ) : (
                    result.name || result.title || 'Untitled'
                  )}
                </h4>

                {result.url && (
                  <p className="text-xs text-green-700 mb-2">{result.url}</p>
                )}

                {result.timestamp && (
                  <p className="text-xs text-gray-500 mb-2">
                    {dayjs(result.timestamp).format('MMM D, YYYY h:mm A')} ({dayjs(result.timestamp).fromNow()})
                  </p>
                )}

                {(result.description || result.snippet) && (
                  <p className="text-sm text-gray-600">
                    {result.description || result.snippet}
                  </p>
                )}

                {result.metadata && (
                  <div className="mt-2">
                    <h5 className="text-xs font-medium text-gray-500 uppercase mb-1">
                      Metadata
                    </h5>
                    <div className="text-sm border border-gray-200 rounded p-2 overflow-auto max-h-60">
                      <ReactJson 
                        src={result.metadata} 
                        name={false}
                        collapsed={1}
                        displayDataTypes={false}
                        enableClipboard={false}
                        theme="rjv-default"
                        style={{ fontSize: '12px' }}
                      />
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      ) : loading ? (
        <div className="bg-white p-4 rounded shadow text-center">
          <p>Searching the web...</p>
        </div>
      ) : (
        query && (
          <div className="bg-white p-4 rounded shadow text-center">
            <p>No web results found for "{query}"</p>
          </div>
        )
      )}
    </div>
  );
};

// Main Search Component
const Search = () => {
  const location = useLocation();

  return (
    <div>
      <div className="mb-6">
        <nav className="flex space-x-4">
          <Link
            to="/intel/search/intel"
            className={`px-3 py-2 rounded font-medium ${
              location.pathname === '/intel/search/intel'
                ? 'bg-blue-100 text-blue-700'
                : 'text-gray-600 hover:text-gray-800 hover:bg-gray-100'
            }`}
          >
            Intel Search
          </Link>
          <Link
            to="/intel/search/web"
            className={`px-3 py-2 rounded font-medium ${
              location.pathname === '/intel/search/web'
                ? 'bg-blue-100 text-blue-700'
                : 'text-gray-600 hover:text-gray-800 hover:bg-gray-100'
            }`}
          >
            Web Search
          </Link>
        </nav>
      </div>

      <Routes>
        <Route path="intel" element={<IntelSearch />} />
        <Route path="web" element={<WebSearch />} />
        <Route
          path="*"
          element={<Navigate to="/intel/search/intel" replace />}
        />
      </Routes>
    </div>
  );
};

export default Search;
